import { supabase } from './supabase';

export interface RadiologyTest {
  id: string;
  code: string;
  name: string;
  modality: 'XR' | 'USG' | 'CT' | 'MRI' | 'MAMMO' | 'DXA' | 'OTHER';
  body_part?: string;
  price: number;
  tat_hours: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RadiologyProtocol {
  id: string;
  test_id: string;
  prep_instructions?: string;
  contrast_required: boolean;
  created_at: string;
}

export interface RadiologyOrder {
  id: string;
  order_no: string;
  patient_id: string;
  ordered_by?: string;
  external_doctor?: string;
  source: 'internal' | 'external' | 'self';
  priority: 'routine' | 'urgent' | 'stat';
  status: 'ordered' | 'scheduled' | 'in_progress' | 'reported' | 'finalized' | 'cancelled';
  scheduled_at?: string;
  notes?: string;
  due_at?: string;
  completed_at?: string;
  tat_minutes?: number;
  created_at: string;
  updated_at: string;
  patient?: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    date_of_birth?: string;
  };
}

export interface RadiologyOrderItem {
  id: string;
  order_id: string;
  test_id: string;
  status: 'ordered' | 'in_progress' | 'reported' | 'finalized' | 'cancelled';
  price: number;
  discount: number;
  created_at: string;
  test?: RadiologyTest;
}

export interface RadiologyStudy {
  id: string;
  order_id: string;
  study_uid?: string;
  viewer_url?: string;
  images_count: number;
  performed_at?: string;
  performed_by?: string;
  remarks?: string;
  created_at: string;
}

export interface RadiologyReport {
  id: string;
  order_id: string;
  report_no: string;
  modality?: string;
  findings?: string;
  impression?: string;
  recommendations?: string;
  template_id?: string;
  pdf_url?: string;
  verification_token?: string;
  finalized_at?: string;
  finalized_by?: string;
  created_at: string;
  updated_at: string;
}

export interface RadiologyReportTemplate {
  id: string;
  modality?: string;
  name: string;
  body: {
    sections: Array<{
      title: string;
      content: string;
    }>;
  };
  active: boolean;
  created_at: string;
}

export interface TATSummaryRadiology {
  modality: string;
  test_name: string;
  total_orders: number;
  avg_tat_minutes: number;
  min_tat_minutes: number;
  max_tat_minutes: number;
  stat_count: number;
  urgent_count: number;
  routine_count: number;
}

export const radiologyTestService = {
  async getAll(): Promise<RadiologyTest[]> {
    const { data, error } = await supabase
      .from('radiology_tests')
      .select('*')
      .eq('active', true)
      .order('name');
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<RadiologyTest | null> {
    const { data, error } = await supabase
      .from('radiology_tests')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async getByModality(modality: string): Promise<RadiologyTest[]> {
    const { data, error } = await supabase
      .from('radiology_tests')
      .select('*')
      .eq('modality', modality)
      .eq('active', true)
      .order('name');
    
    if (error) throw error;
    return data || [];
  },

  async getProtocol(testId: string): Promise<RadiologyProtocol | null> {
    const { data, error } = await supabase
      .from('radiology_protocols')
      .select('*')
      .eq('test_id', testId)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },
};

export const radiologyOrderService = {
  async getAll(): Promise<RadiologyOrder[]> {
    const { data, error } = await supabase
      .from('radiology_orders')
      .select(`
        *,
        patient:patients(id, name, email, phone, date_of_birth)
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<RadiologyOrder | null> {
    const { data, error } = await supabase
      .from('radiology_orders')
      .select(`
        *,
        patient:patients(id, name, email, phone, date_of_birth)
      `)
      .eq('id', id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async create(order: Omit<RadiologyOrder, 'id' | 'order_no' | 'created_at' | 'updated_at'>): Promise<RadiologyOrder> {
    const { data, error } = await supabase
      .from('radiology_orders')
      .insert(order)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateStatus(id: string, status: RadiologyOrder['status']): Promise<RadiologyOrder> {
    const { data, error } = await supabase
      .from('radiology_orders')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async schedule(id: string, scheduledAt: string): Promise<RadiologyOrder> {
    const { data, error } = await supabase
      .from('radiology_orders')
      .update({ 
        scheduled_at: scheduledAt,
        status: 'scheduled',
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },
};

export const radiologyOrderItemService = {
  async getByOrder(orderId: string): Promise<RadiologyOrderItem[]> {
    const { data, error } = await supabase
      .from('radiology_order_items')
      .select('*, test:radiology_tests(*)')
      .eq('order_id', orderId);
    
    if (error) throw error;
    return data || [];
  },

  async create(item: Omit<RadiologyOrderItem, 'id' | 'created_at'>): Promise<RadiologyOrderItem> {
    const { data, error } = await supabase
      .from('radiology_order_items')
      .insert(item)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateStatus(id: string, status: RadiologyOrderItem['status']): Promise<RadiologyOrderItem> {
    const { data, error } = await supabase
      .from('radiology_order_items')
      .update({ status })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },
};

export const radiologyStudyService = {
  async getByOrder(orderId: string): Promise<RadiologyStudy | null> {
    const { data, error } = await supabase
      .from('radiology_studies')
      .select('*')
      .eq('order_id', orderId)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async create(study: Omit<RadiologyStudy, 'id' | 'created_at'>): Promise<RadiologyStudy> {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('radiology_studies')
      .insert({
        ...study,
        performed_by: userData?.user?.id,
        performed_at: new Date().toISOString(),
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, study: Partial<RadiologyStudy>): Promise<RadiologyStudy> {
    const { data, error } = await supabase
      .from('radiology_studies')
      .update(study)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },
};

export const radiologyReportService = {
  async getByOrder(orderId: string): Promise<RadiologyReport | null> {
    const { data, error } = await supabase
      .from('radiology_reports')
      .select('*')
      .eq('order_id', orderId)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async getById(id: string): Promise<RadiologyReport | null> {
    const { data, error } = await supabase
      .from('radiology_reports')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async create(report: Omit<RadiologyReport, 'id' | 'created_at' | 'updated_at'>): Promise<RadiologyReport> {
    const { data: userData } = await supabase.auth.getUser();
    
    const verificationToken = Math.random().toString(36).substring(2, 15);
    
    const { data, error } = await supabase
      .from('radiology_reports')
      .insert({
        ...report,
        verification_token: verificationToken,
        finalized_by: userData?.user?.id,
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, report: Partial<RadiologyReport>): Promise<RadiologyReport> {
    const { data, error } = await supabase
      .from('radiology_reports')
      .update({
        ...report,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async finalize(id: string): Promise<RadiologyReport> {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('radiology_reports')
      .update({
        finalized_at: new Date().toISOString(),
        finalized_by: userData?.user?.id,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async uploadPDF(orderId: string, pdfBlob: Blob): Promise<string> {
    const fileName = `radiology-report-${orderId}-${Date.now()}.pdf`;
    const { data, error } = await supabase.storage
      .from('radiology-reports')
      .upload(fileName, pdfBlob, {
        contentType: 'application/pdf',
        upsert: false
      });
    
    if (error) throw error;
    return fileName;
  },

  async getSignedUrl(fileName: string, expiresIn = 3600): Promise<string> {
    const { data, error } = await supabase.storage
      .from('radiology-reports')
      .createSignedUrl(fileName, expiresIn);
    
    if (error) throw error;
    return data.signedUrl;
  },

  async verifyByToken(reportId: string, token: string): Promise<RadiologyReport | null> {
    const { data, error } = await supabase
      .from('radiology_reports')
      .select('*')
      .eq('id', reportId)
      .eq('verification_token', token)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },
};

export const radiologyReportTemplateService = {
  async getAll(): Promise<RadiologyReportTemplate[]> {
    const { data, error } = await supabase
      .from('radiology_report_templates')
      .select('*')
      .eq('active', true)
      .order('name');
    
    if (error) throw error;
    return data || [];
  },

  async getByModality(modality: string): Promise<RadiologyReportTemplate[]> {
    const { data, error } = await supabase
      .from('radiology_report_templates')
      .select('*')
      .eq('modality', modality)
      .eq('active', true)
      .order('name');
    
    if (error) throw error;
    return data || [];
  },
};

export const radiologyTATService = {
  async getSummary(filters?: {
    modality?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<TATSummaryRadiology[]> {
    let query = supabase
      .from('radiology_tat_summary')
      .select('*')
      .order('avg_tat_minutes', { ascending: false });

    if (filters?.modality) {
      query = query.eq('modality', filters.modality);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  async getOrdersWithTAT(filters?: {
    priority?: string;
    overdue?: boolean;
    startDate?: string;
    endDate?: string;
  }): Promise<any[]> {
    let query = supabase
      .from('radiology_orders')
      .select(`
        *,
        patient:patients(id, name),
        items:radiology_order_items(
          *,
          test:radiology_tests(code, name, tat_hours)
        )
      `)
      .not('tat_minutes', 'is', null)
      .order('created_at', { ascending: false });

    if (filters?.priority) {
      query = query.eq('priority', filters.priority);
    }

    if (filters?.overdue) {
      query = query.lt('due_at', new Date().toISOString());
    }

    if (filters?.startDate) {
      query = query.gte('created_at', filters.startDate);
    }

    if (filters?.endDate) {
      query = query.lte('created_at', filters.endDate);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },
};

export async function generateRadiologyReportPDFBlob(
  reportData: {
    reportNo: string;
    patient?: { name: string; phone?: string };
    orderId: string;
    modality?: string;
    findings?: string;
    impression?: string;
    recommendations?: string;
    viewerUrl?: string;
    imagesCount?: number;
    finalizedBy: string;
    finalizedAt: Date;
    priority?: string;
    tatMinutes?: number;
  }
): Promise<Blob> {
  const { default: jsPDF } = await import('jspdf');
  const doc = new jsPDF();
  
  doc.setFontSize(20);
  doc.text('Radiology Report', 105, 20, { align: 'center' });
  
  doc.setFontSize(12);
  doc.text(`Report No: ${reportData.reportNo}`, 20, 40);
  doc.text(`Patient: ${reportData.patient?.name || 'Unknown'}`, 20, 50);
  doc.text(`Phone: ${reportData.patient?.phone || 'N/A'}`, 20, 60);
  doc.text(`Modality: ${reportData.modality || 'N/A'}`, 20, 70);
  doc.text(`Date: ${reportData.finalizedAt.toLocaleDateString()}`, 20, 80);
  
  let yPos = 100;
  
  if (reportData.findings) {
    doc.setFontSize(14);
    doc.text('Findings', 20, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    const findingsLines = doc.splitTextToSize(reportData.findings, 170);
    doc.text(findingsLines, 20, yPos);
    yPos += findingsLines.length * 6 + 10;
  }
  
  if (reportData.impression) {
    doc.setFontSize(14);
    doc.text('Impression', 20, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    const impressionLines = doc.splitTextToSize(reportData.impression, 170);
    doc.text(impressionLines, 20, yPos);
    yPos += impressionLines.length * 6 + 10;
  }
  
  if (reportData.recommendations) {
    doc.setFontSize(14);
    doc.text('Recommendations', 20, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    const recLines = doc.splitTextToSize(reportData.recommendations, 170);
    doc.text(recLines, 20, yPos);
    yPos += recLines.length * 6 + 10;
  }
  
  if (reportData.viewerUrl) {
    yPos += 5;
    doc.setFontSize(10);
    doc.text(`Images available via PACS viewer (${reportData.imagesCount || 0} images)`, 20, yPos);
    yPos += 6;
    doc.setFontSize(8);
    doc.text(reportData.viewerUrl, 20, yPos, { maxWidth: 170 });
  }
  
  const qrData = `${window.location.origin}/verify/rad/${reportData.orderId}?token=${reportData.reportNo}`;
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFontSize(8);
  doc.text('Scan to verify:', 20, pageHeight - 35);
  doc.text(qrData, 20, pageHeight - 28, { maxWidth: 170 });
  
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text('This is a computer-generated report and does not require a physical signature.', pageWidth / 2, pageHeight - 20, { align: 'center' });
  doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, pageHeight - 15, { align: 'center' });
  
  if (reportData.priority || reportData.tatMinutes) {
    doc.setFontSize(8);
    const footerY = pageHeight - 10;
    if (reportData.priority) {
      doc.text(`Priority: ${reportData.priority.toUpperCase()}`, 15, footerY);
    }
    if (reportData.tatMinutes) {
      const tatHours = Math.floor(reportData.tatMinutes / 60);
      const tatMins = Math.floor(reportData.tatMinutes % 60);
      doc.text(`TAT: ${tatHours}h ${tatMins}m`, pageWidth - 15, footerY, { align: 'right' });
    }
  }
  
  return doc.output('blob');
}
